import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { EducationalResourcesService } from "@/services/educationalResources.service";
import {
  EducationalResource,
  EducationalResourceFilters,
  EducationalResourcePayload,
} from "@/domain/educationalResource.interface";

export const EDUCATIONAL_RESOURCES_KEY = "pret-educational-resources";

/** The filters are part of the key, so any filter/page/sort change refetches. */
export const useEducationalResources = (
  filters: EducationalResourceFilters
) => {
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: [EDUCATIONAL_RESOURCES_KEY, filters],
    queryFn: () => EducationalResourcesService.search(filters),
    placeholderData: keepPreviousData,
  });

  return {
    educationalResources: data,
    isLoadingEducationalResources: isLoading,
    isFetchingEducationalResources: isFetching,
    educationalResourcesError: error,
    refetchEducationalResources: refetch,
  };
};

/** The API caps pageSize at 100 (GetEducationalResources.dto). */
export const CATALOGUE_PAGE_SIZE = 100;
/** Safety stop for a misbehaving totalCount: 50 pages = 5,000 resources. */
const CATALOGUE_MAX_PAGES = 50;

/**
 * PRET-2046 — every non-deleted resource of the account, all pages.
 *
 * No status filter on purpose: the programme modal offers only the ACTIVE ones as options, but
 * needs the INACTIVE ones too so an inactive assignment is still shown (and kept) when the
 * programme is reopened. Deleted resources are excluded by the API itself.
 */
export const fetchAllEducationalResources = async (): Promise<
  EducationalResource[]
> => {
  const all: EducationalResource[] = [];
  for (let pageNumber = 0; pageNumber < CATALOGUE_MAX_PAGES; pageNumber++) {
    const page = await EducationalResourcesService.search({
      search: "",
      categoryId: "",
      type: "",
      status: "",
      pageNumber,
      pageSize: CATALOGUE_PAGE_SIZE,
      sortField: "createdAt",
      sortOrder: "desc",
    });
    const list = page?.list || [];
    all.push(...list);
    if (list.length === 0 || all.length >= (page?.totalCount ?? 0)) break;
  }
  return all;
};

export const useAllEducationalResources = (enabled = true) => {
  const { data, isLoading, isSuccess, isFetching, error } = useQuery({
    // Under the shared key, so creating/editing a resource refreshes this list too.
    queryKey: [EDUCATIONAL_RESOURCES_KEY, "all"],
    queryFn: fetchAllEducationalResources,
    enabled,
  });

  return {
    allEducationalResources: data,
    isLoadingAllEducationalResources: isLoading,
    isAllEducationalResourcesLoaded: isSuccess,
    // True while any fetch (first load or background refetch of a cached list) is in flight.
    isFetchingAllEducationalResources: isFetching,
    allEducationalResourcesError: error,
  };
};

export const useCreateEducationalResource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EducationalResourcePayload) =>
      EducationalResourcesService.create(data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [EDUCATIONAL_RESOURCES_KEY] }),
  });
};

export const useUpdateEducationalResource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: EducationalResourcePayload;
    }) => EducationalResourcesService.update({ id, data }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [EDUCATIONAL_RESOURCES_KEY] }),
  });
};

export const useDeleteEducationalResource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updatedBy }: { id: string; updatedBy?: string }) =>
      EducationalResourcesService.remove({ id, updatedBy }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [EDUCATIONAL_RESOURCES_KEY] }),
  });
};
